import forEach from "lodash/forEach";

export const parseRobotsDirective = (directive: RobotsDirectives) => {
  return {
    index: !directive.includes("NOINDEX"),
    follow: !directive.includes("NOFOLLOW"),
  };
};

import * as icons from "@spoctech/icons";
import { load } from "cheerio";

export const getIconNames = () => Object.keys(icons);

export const sanitizeEmail = (email: string): string => {
  if (!email) return "";
  return email
    .trim()
    .toLowerCase()
    .replace(/[\[\]]/g, "")
    .replace(/\(at\)/g, "@")
    .replace(/\(dot\)/g, ".");
};

export const unOrderedListToArray = (str: string): string[] => {
  if (!str) return [];
  const $ = load(str);
  const items: string[] = [];
  $("ul li").each(function () {
    items.push($(this).text());
  });
  return items;
};

export const checkFirstString = (data: string): string => {
  if (!data) return "";
  let r = "";
  if (data.charAt(0) == "+" || data.charAt(0) === "1") {
    r = `tel:${data.trim()}`;
  } else if (validateEmail(data)) {
    r = `mailto:${sanitizeEmail(data)}`;
  } else {
    r = "https://maps.app.goo.gl/akaC1Ko9ULseycrK7";
  }
  return r;
};

export const validateEmail = (email: string): string | null => {
  if (!email || typeof email !== "string") return null;

  const sanitizedEmail = email
    .trim()
    .toLowerCase()
    .replace(/\(at\)/g, "@")
    .replace(/\(dot\)/g, ".");

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailRegex.test(sanitizedEmail) ? sanitizedEmail : null;
};

export const hasData = (
  data: Record<string, any> | null | undefined
): boolean => {
  if (!data || typeof data !== "object") return false;

  const ignoredKeys = [
    "is_default_content",
    "template_default_content_id",
    "is_dynamic",
  ];

  const hasOtherProps = Object.keys(data).some(
    (key) => !ignoredKeys.includes(key)
  );

  return hasOtherProps;
};

export function errorsToObject(errors = []) {
  const newErrorObj = {};
  if (Array.isArray(errors) && errors.length > 0) {
    forEach(errors, (value, key) => {
      Object.assign(newErrorObj, { [key]: value[0] });
    });
    return newErrorObj;
  } else {
    const flatErrors = Object.fromEntries(
      Object.entries(errors).map(([key, messages]) => [key, messages[0]])
    );
    return flatErrors;
  }
}

export const formatCurrency = (
  value: number = 0,
  currency: string = "USD",
  display: "code" | "symbol" = "symbol"
) => {
  const formatted = value.toLocaleString("en-US", {
    style: "currency",
    currency,
    currencyDisplay: display, // "code" -> USD, "symbol" -> $
  });

  // Insert a space between the currency and the number
  return formatted.replace(/(\p{Sc}|[A-Z]{3})(\d)/u, "$1 $2");
};

export function getBackgroundImage(srcSet = "") {
  const imageSet = srcSet
    .split(", ")
    .map((str) => {
      const [url, dpi] = str.split(" ");
      return `url("${url}") ${dpi}`;
    })
    .join(", ");
  return `image-set(${imageSet})`;
}

export function getWorkloadInISO(workload: string): string {
  if (!workload) return "PT32H"; // Default to 32 hours if no workload is provided

  const str = workload.trim().toLowerCase();

  // Match number + unit (e.g. "2 days", "32 hrs", "4 weeks")
  const match = str.match(
    /(\d+)\s*(day|days|d|hour|hours|hrs|h|week|weeks|w|month|months|m|year|years|y)/
  );

  if (!match) return "PT32H"; // Default to 32 hours if no workload is provided

  const value = parseInt(match[1], 10);
  const unit = match[2];

  switch (true) {
    case /day|d/.test(unit):
      return `P${value}D`; // Period in Days
    case /hour|hrs|h/.test(unit):
      return `PT${value}H`; // Time in Hours
    case /week|w/.test(unit):
      return `P${value}W`; // Period in Weeks
    case /month|m/.test(unit):
      return `P${value}M`; // Period in Months
    case /year|y/.test(unit):
      return `P${value}Y`; // Period in Years
    default:
      return "PT32H"; // Default to 32 hours if no workload is provided
  }
}
